import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  User,
  Clock,
  MapPin,
  Star,
  AlertTriangle,
  X,
  Trash2
} from 'lucide-react';
import { 
  format, 
  addDays, 
  startOfWeek, 
  isSameDay, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  getDay,
  addMonths,
  subMonths,
  isToday
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseLocalDateTime, formatTime, isSameDayLocal } from '../utils/dateUtils';

// ============== FERIADOS 2025-2027 ==============
// Feriados Federais, Estaduais (SP) e Municipais (Sorocaba)
const FERIADOS = {
  // 2025
  '2025-01-01': { nome: 'Confraternização Universal', tipo: 'federal' },
  '2025-03-03': { nome: 'Carnaval', tipo: 'federal' },
  '2025-03-04': { nome: 'Carnaval', tipo: 'federal' },
  '2025-04-18': { nome: 'Sexta-feira Santa', tipo: 'federal' },
  '2025-04-21': { nome: 'Tiradentes', tipo: 'federal' },
  '2025-05-01': { nome: 'Dia do Trabalho', tipo: 'federal' },
  '2025-06-19': { nome: 'Corpus Christi', tipo: 'federal' },
  '2025-07-09': { nome: 'Revolução Constitucionalista', tipo: 'estadual' },
  '2025-08-15': { nome: 'Aniversário de Sorocaba', tipo: 'municipal' },
  '2025-09-07': { nome: 'Independência do Brasil', tipo: 'federal' },
  '2025-10-12': { nome: 'Nossa Sra. Aparecida', tipo: 'federal' },
  '2025-11-02': { nome: 'Finados', tipo: 'federal' },
  '2025-11-15': { nome: 'Proclamação da República', tipo: 'federal' },
  '2025-11-20': { nome: 'Consciência Negra', tipo: 'federal' },
  '2025-12-25': { nome: 'Natal', tipo: 'federal' },
  
  // 2026
  '2026-01-01': { nome: 'Confraternização Universal', tipo: 'federal' },
  '2026-02-16': { nome: 'Carnaval', tipo: 'federal' },
  '2026-02-17': { nome: 'Carnaval', tipo: 'federal' },
  '2026-04-03': { nome: 'Sexta-feira Santa', tipo: 'federal' },
  '2026-04-21': { nome: 'Tiradentes', tipo: 'federal' },
  '2026-05-01': { nome: 'Dia do Trabalho', tipo: 'federal' },
  '2026-06-04': { nome: 'Corpus Christi', tipo: 'federal' },
  '2026-07-09': { nome: 'Revolução Constitucionalista', tipo: 'estadual' },
  '2026-08-15': { nome: 'Aniversário de Sorocaba', tipo: 'municipal' },
  '2026-09-07': { nome: 'Independência do Brasil', tipo: 'federal' },
  '2026-10-12': { nome: 'Nossa Sra. Aparecida', tipo: 'federal' },
  '2026-11-02': { nome: 'Finados', tipo: 'federal' },
  '2026-11-15': { nome: 'Proclamação da República', tipo: 'federal' },
  '2026-11-20': { nome: 'Consciência Negra', tipo: 'federal' },
  '2026-12-25': { nome: 'Natal', tipo: 'federal' },
  
  // 2027
  '2027-01-01': { nome: 'Confraternização Universal', tipo: 'federal' },
  '2027-02-08': { nome: 'Carnaval', tipo: 'federal' },
  '2027-02-09': { nome: 'Carnaval', tipo: 'federal' },
  '2027-03-26': { nome: 'Sexta-feira Santa', tipo: 'federal' },
  '2027-04-21': { nome: 'Tiradentes', tipo: 'federal' },
  '2027-05-01': { nome: 'Dia do Trabalho', tipo: 'federal' },
  '2027-05-27': { nome: 'Corpus Christi', tipo: 'federal' },
  '2027-07-09': { nome: 'Revolução Constitucionalista', tipo: 'estadual' },
  '2027-08-15': { nome: 'Aniversário de Sorocaba', tipo: 'municipal' },
  '2027-09-07': { nome: 'Independência do Brasil', tipo: 'federal' },
  '2027-10-12': { nome: 'Nossa Sra. Aparecida', tipo: 'federal' },
  '2027-11-02': { nome: 'Finados', tipo: 'federal' },
  '2027-11-15': { nome: 'Proclamação da República', tipo: 'federal' },
  '2027-11-20': { nome: 'Consciência Negra', tipo: 'federal' },
  '2027-12-25': { nome: 'Natal', tipo: 'federal' },
};

const getFeriado = (date) => {
  const key = format(date, 'yyyy-MM-dd');
  return FERIADOS[key] || null;
};

const FERIADO_COLORS = {
  federal: { bg: '#FEE8E8', text: '#B75D69', border: '#B75D69' },
  estadual: { bg: '#FDF6EE', text: '#D4A373', border: '#D4A373' },
  municipal: { bg: '#E8F0EB', text: '#4A6B5D', border: '#4A6B5D' }
};

// Configuração de horários
const HORARIOS = [];
for (let h = 7; h <= 20; h++) {
  HORARIOS.push(`${h.toString().padStart(2, '0')}:00`);
  if (h < 20) HORARIOS.push(`${h.toString().padStart(2, '0')}:30`);
}

const STATUS_COLORS = {
  agendado: { bg: '#E8F0EB', border: '#4A6B5D', text: '#4A6B5D' },
  aguardando: { bg: '#FDF6EE', border: '#D4A373', text: '#D4A373' },
  em_atendimento: { bg: '#E8F0EB', border: '#4A6B5D', text: '#4A6B5D' },
  finalizado: { bg: '#F3F0EA', border: '#8A9992', text: '#5C6B64' },
  cancelado: { bg: '#FEE8E8', border: '#B75D69', text: '#B75D69' },
  falta: { bg: '#FEE8E8', border: '#B75D69', text: '#B75D69' }
};

const AgendaTimelineView = ({ 
  agendamentos, 
  profissionais, 
  salas,
  selectedDate,
  viewMode,
  filtros,
  onAgendamentoClick,
  onDeleteClick,
  canDelete = false
}) => {
  // Detectar mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Filtrar agendamentos baseado nos filtros
  const agendamentosFiltrados = useMemo(() => {
    let filtered = agendamentos.filter(ag => 
      isSameDayLocal(ag.data_hora_inicio, selectedDate)
    );
    
    if (filtros.profissional) {
      filtered = filtered.filter(ag => ag.profissional_id === filtros.profissional);
    }
    
    if (filtros.sala) {
      filtered = filtered.filter(ag => ag.sala_id === filtros.sala);
    }
    
    return filtered;
  }, [agendamentos, selectedDate, filtros]);

  // Agrupar por profissional ou sala
  const grupos = useMemo(() => {
    if (viewMode === 'profissional') {
      const map = {};
      profissionais.forEach(p => {
        map[p.id] = {
          id: p.id,
          nome: p.usuarios?.nome || 'Profissional',
          especialidade: p.especialidade,
          agendamentos: agendamentosFiltrados.filter(ag => ag.profissional_id === p.id)
        };
      });
      return Object.values(map).filter(g => g.agendamentos.length > 0 || !filtros.profissional);
    } else {
      const map = {};
      salas.forEach(s => {
        map[s.id] = {
          id: s.id,
          nome: `Sala ${s.numero}`,
          descricao: s.nome,
          agendamentos: agendamentosFiltrados.filter(ag => ag.sala_id === s.id)
        };
      });
      return Object.values(map);
    }
  }, [viewMode, profissionais, salas, agendamentosFiltrados, filtros]);

  // Converter horário para posição no grid
  const getSlotPosition = (timeStr) => {
    const [h, m] = timeStr.split(':').map(Number);
    const startHour = 7;
    const slot = (h - startHour) * 2 + (m >= 30 ? 1 : 0);
    return Math.max(0, slot);
  };

  // Calcular duração em slots
  const getSlotDuration = (inicio, fim) => {
    const posInicio = getSlotPosition(inicio);
    const posFim = getSlotPosition(fim);
    return Math.max(1, posFim - posInicio);
  };

  return (
    <div className="relative">
      {/* Container com scroll horizontal isolado */}
      <div className="overflow-x-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div style={{ minWidth: isMobile ? '600px' : '900px' }}>
          {/* Header com horários */}
          <div className="flex border-b sticky top-0 z-10" style={{ backgroundColor: '#FDFBF7', borderColor: '#EAE6DF' }}>
            <div 
              className="w-28 md:w-48 flex-shrink-0 p-2 border-r sticky left-0 z-20"
              style={{ borderColor: '#EAE6DF', backgroundColor: '#FDFBF7' }}
            >
              <span className="text-xs md:text-sm font-medium" style={{ color: '#5C6B64' }}>
                {viewMode === 'profissional' ? 'Profissional' : 'Sala'}
              </span>
            </div>
            <div className="flex-1 flex">
              {HORARIOS.map((h, idx) => (
                <div 
                  key={h} 
                  className="flex-shrink-0 w-12 md:w-16 text-center py-2 text-xs border-l"
                  style={{ 
                    borderColor: '#EAE6DF', 
                    color: idx % 2 === 0 ? '#2C3632' : '#8A9992',
                    fontWeight: idx % 2 === 0 ? 500 : 400
                  }}
                >
                  {idx % 2 === 0 ? h : ''}
                </div>
              ))}
            </div>
          </div>

          {/* Linhas por grupo */}
          <ScrollArea className="h-[calc(100vh-350px)]">
            {grupos.length === 0 ? (
              <div className="text-center py-12" style={{ color: '#5C6B64' }}>
                Nenhum agendamento para esta data
              </div>
            ) : (
              grupos.map((grupo) => {
                const slotWidth = isMobile ? 48 : 64;
                
                return (
                  <div 
                    key={grupo.id} 
                    className="flex border-b min-h-[60px]"
                    style={{ borderColor: '#EAE6DF' }}
                  >
                    {/* Nome do grupo - STICKY */}
                    <div 
                      className="w-28 md:w-48 flex-shrink-0 p-2 border-r flex flex-col justify-center sticky left-0 z-10"
                      style={{ borderColor: '#EAE6DF', backgroundColor: '#F9F7F3' }}
                    >
                      <span className="text-xs md:text-sm font-medium truncate" style={{ color: '#2C3632' }}>
                        {grupo.nome}
                      </span>
                      {grupo.especialidade && (
                        <span className="text-xs hidden md:block" style={{ color: '#5C6B64' }}>
                          {grupo.especialidade}
                        </span>
                      )}
                      {grupo.descricao && (
                        <span className="text-xs hidden md:block" style={{ color: '#5C6B64' }}>
                          {grupo.descricao}
                        </span>
                      )}
                    </div>

                    {/* Grid de horários */}
                    <div className="flex-1 flex relative">
                      {/* Background grid */}
                      {HORARIOS.map((h) => (
                        <div 
                          key={h} 
                          className="flex-shrink-0 w-12 md:w-16 border-l"
                          style={{ borderColor: '#F3F0EA' }}
                        />
                      ))}

                      {/* Agendamentos posicionados */}
                      {grupo.agendamentos.map((ag) => {
                        const horaInicio = formatTime(ag.data_hora_inicio);
                        const horaFim = formatTime(ag.data_hora_fim);
                        const posicao = getSlotPosition(horaInicio);
                        const duracao = getSlotDuration(horaInicio, horaFim);
                        const status = STATUS_COLORS[ag.status] || STATUS_COLORS.agendado;

                        return (
                          <div
                            key={ag.id}
                            className="absolute top-1 bottom-1 rounded-lg p-1 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                            style={{
                              left: `${posicao * slotWidth}px`,
                              width: `${duracao * slotWidth - 4}px`,
                              backgroundColor: status.bg,
                              borderLeft: `3px solid ${status.border}`
                            }}
                            title={`${ag.pacientes?.nome_completo || 'Paciente'} - ${horaInicio} às ${horaFim}`}
                            onClick={() => onAgendamentoClick && onAgendamentoClick(ag)}
                          >
                            <p 
                              className="text-xs font-medium truncate" 
                              style={{ color: status.text }}
                            >
                              {ag.pacientes?.nome_completo?.split(' ')[0] || 'Paciente'}
                            </p>
                            <p className="text-[10px] truncate hidden md:block" style={{ color: '#5C6B64' }}>
                              {horaInicio} - {horaFim}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

const AgendaCalendarView = ({ onAgendamentoClick, onDeleteClick, canDelete = false, refreshKey = 0 }) => {
  const { session, isAuthenticated, isProfissional } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [agendamentos, setAgendamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null); // Dia clicado para ver detalhes
  
  const fetchController = useRef(null);
  
  // Fetch agendamentos do mês
  useEffect(() => {
    const token = session?.access_token;
    if (!isAuthenticated || !token) return;
    
    if (fetchController.current) {
      fetchController.current.abort();
    }
    fetchController.current = new AbortController();
    
    const fetchData = async () => {
      setLoading(true);
      
      const API = process.env.REACT_APP_BACKEND_URL;
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
        signal: fetchController.current.signal
      };
      
      try {
        const inicio = startOfMonth(currentMonth);
        const fim = endOfMonth(currentMonth);
        
        const res = await axios.get(
          `${API}/api/agendamentos?data_inicio=${format(inicio, 'yyyy-MM-dd')}&data_fim=${format(fim, 'yyyy-MM-dd')}`, 
          config
        );
        
        setAgendamentos(res.data || []);
      } catch (err) {
        if (err.name !== 'CanceledError') {
          console.error('Erro ao carregar agendamentos:', err);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
    
    return () => {
      if (fetchController.current) {
        fetchController.current.abort();
      }
    };
  }, [currentMonth, isAuthenticated, session?.access_token, refreshKey]);

  // Dias do mês para o grid
  const diasDoMes = useMemo(() => {
    const inicio = startOfMonth(currentMonth);
    const fim = endOfMonth(currentMonth);
    const dias = eachDayOfInterval({ start: inicio, end: fim });
    
    // Adicionar dias vazios no início para alinhar com dia da semana
    // getDay: 0=domingo, 1=segunda, ..., 6=sábado
    const primeiroDiaSemana = getDay(inicio);
    const diasVaziosInicio = Array(primeiroDiaSemana).fill(null);
    
    return [...diasVaziosInicio, ...dias];
  }, [currentMonth]);

  // Contar agendamentos por dia
  const agendamentosPorDia = useMemo(() => {
    const map = {};
    agendamentos.forEach(ag => {
      const dataLocal = parseLocalDateTime(ag.data_hora_inicio);
      if (!dataLocal) return;
      const diaKey = format(dataLocal, 'yyyy-MM-dd');
      if (!map[diaKey]) map[diaKey] = [];
      map[diaKey].push(ag);
    });
    return map;
  }, [agendamentos]);

  // Agendamentos do dia selecionado
  const agendamentosDoDiaSelecionado = useMemo(() => {
    if (!selectedDay) return [];
    const key = format(selectedDay, 'yyyy-MM-dd');
    return (agendamentosPorDia[key] || []).sort((a, b) => 
      parseLocalDateTime(a.data_hora_inicio) - parseLocalDateTime(b.data_hora_inicio)
    );
  }, [selectedDay, agendamentosPorDia]);

  // Navegação de meses
  const irMesAnterior = () => setCurrentMonth(prev => subMonths(prev, 1));
  const irProximoMes = () => setCurrentMonth(prev => addMonths(prev, 1));
  const irParaHoje = () => {
    setCurrentMonth(new Date());
    setSelectedDay(new Date());
  };

  const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16" />
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="agenda-calendar-view">
      {/* Header - Estilo Calendário de Borracharia */}
      <Card className="border-0 shadow-sm" style={{ backgroundColor: '#FDFBF7' }}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={irMesAnterior}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={irParaHoje}>
                Hoje
              </Button>
              <Button variant="outline" size="icon" onClick={irProximoMes}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            
            <h2 
              className="text-xl md:text-2xl font-bold text-center capitalize"
              style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}
            >
              {format(currentMonth, "MMMM 'de' yyyy", { locale: ptBR })}
            </h2>
            
            <div className="w-24" /> {/* Spacer para centralizar título */}
          </div>
          
          {/* Legenda de Feriados */}
          <div className="flex flex-wrap gap-3 mt-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: FERIADO_COLORS.federal.bg, border: `1px solid ${FERIADO_COLORS.federal.border}` }} />
              <span style={{ color: '#5C6B64' }}>Federal</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: FERIADO_COLORS.estadual.bg, border: `1px solid ${FERIADO_COLORS.estadual.border}` }} />
              <span style={{ color: '#5C6B64' }}>Estadual</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: FERIADO_COLORS.municipal.bg, border: `1px solid ${FERIADO_COLORS.municipal.border}` }} />
              <span style={{ color: '#5C6B64' }}>Municipal</span>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-2 md:p-4">
          {/* Cabeçalho dias da semana */}
          <div className="grid grid-cols-7 mb-1">
            {DIAS_SEMANA.map((dia, idx) => (
              <div 
                key={dia} 
                className="text-center py-2 text-xs md:text-sm font-semibold"
                style={{ 
                  color: idx === 0 ? '#B75D69' : '#5C6B64',
                  fontFamily: 'Manrope, sans-serif'
                }}
              >
                {dia}
              </div>
            ))}
          </div>
          
          {/* Grid do Mês */}
          <div className="grid grid-cols-7 gap-1">
            {diasDoMes.map((dia, idx) => {
              if (!dia) {
                return <div key={`empty-${idx}`} className="aspect-square" />;
              }
              
              const diaKey = format(dia, 'yyyy-MM-dd');
              const feriado = getFeriado(dia);
              const qtdAgendamentos = agendamentosPorDia[diaKey]?.length || 0;
              const ehHoje = isToday(dia);
              const ehDomingo = getDay(dia) === 0;
              const ehSelecionado = selectedDay && isSameDay(dia, selectedDay);
              
              return (
                <div
                  key={diaKey}
                  className={`
                    aspect-square p-1 rounded-lg cursor-pointer transition-all
                    hover:ring-2 hover:ring-offset-1
                    ${ehSelecionado ? 'ring-2 ring-offset-1' : ''}
                  `}
                  style={{
                    backgroundColor: feriado 
                      ? FERIADO_COLORS[feriado.tipo].bg 
                      : ehHoje 
                        ? '#E8F0EB' 
                        : ehDomingo 
                          ? '#FEF9F3' 
                          : '#FFFFFF',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: feriado 
                      ? FERIADO_COLORS[feriado.tipo].border 
                      : ehHoje 
                        ? '#4A6B5D' 
                        : '#EAE6DF',
                    ringColor: '#4A6B5D'
                  }}
                  onClick={() => setSelectedDay(dia)}
                  title={feriado ? feriado.nome : ''}
                >
                  <div className="h-full flex flex-col">
                    {/* Número do dia */}
                    <div 
                      className={`text-sm md:text-base font-bold ${ehHoje ? 'text-white rounded-full w-6 h-6 md:w-7 md:h-7 flex items-center justify-center mx-auto' : ''}`}
                      style={{ 
                        color: ehHoje ? '#FFFFFF' : feriado ? FERIADO_COLORS[feriado.tipo].text : ehDomingo ? '#B75D69' : '#2C3632',
                        backgroundColor: ehHoje ? '#4A6B5D' : 'transparent'
                      }}
                    >
                      {format(dia, 'd')}
                    </div>
                    
                    {/* Nome do feriado (truncado) */}
                    {feriado && (
                      <div 
                        className="text-[8px] md:text-[10px] leading-tight truncate mt-auto"
                        style={{ color: FERIADO_COLORS[feriado.tipo].text }}
                        title={feriado.nome}
                      >
                        {feriado.nome}
                      </div>
                    )}
                    
                    {/* Badge de agendamentos */}
                    {qtdAgendamentos > 0 && !feriado && (
                      <div 
                        className="mt-auto mx-auto text-[10px] md:text-xs px-1.5 py-0.5 rounded-full font-medium"
                        style={{ backgroundColor: '#4A6B5D', color: '#FFFFFF' }}
                      >
                        {qtdAgendamentos}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Detalhes do dia selecionado */}
      {selectedDay && (
        <Card className="border mb-20" style={{ borderColor: '#EAE6DF' }}>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg" style={{ color: '#2C3632', fontFamily: 'Manrope, sans-serif' }}>
                {format(selectedDay, "EEEE, d 'de' MMMM", { locale: ptBR })}
              </CardTitle>
              {getFeriado(selectedDay) && (
                <Badge 
                  className="mt-1"
                  style={{ 
                    backgroundColor: FERIADO_COLORS[getFeriado(selectedDay).tipo].bg,
                    color: FERIADO_COLORS[getFeriado(selectedDay).tipo].text
                  }}
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {getFeriado(selectedDay).nome}
                </Badge>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setSelectedDay(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          
          <CardContent className="pb-6">
            {agendamentosDoDiaSelecionado.length === 0 ? (
              <p className="text-center py-6" style={{ color: '#5C6B64' }}>
                {getFeriado(selectedDay) 
                  ? 'Feriado - sem agendamentos' 
                  : 'Nenhum agendamento neste dia'
                }
              </p>
            ) : (
              <div 
                className="overflow-y-auto overscroll-contain"
                style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '200px' }}
              >
                <div className="space-y-2 pb-4 pr-1">
                  {agendamentosDoDiaSelecionado.map(ag => {
                    const horaInicio = formatTime(ag.data_hora_inicio);
                    const horaFim = formatTime(ag.data_hora_fim);
                    const status = STATUS_COLORS[ag.status] || STATUS_COLORS.agendado;
                    
                    return (
                      <div
                        key={ag.id}
                        className="p-3 rounded-lg cursor-pointer hover:shadow-md transition-shadow"
                        style={{ 
                          backgroundColor: status.bg,
                          borderLeft: `4px solid ${status.border}`
                        }}
                        onClick={() => onAgendamentoClick?.(ag)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" style={{ color: status.text }} />
                            <span className="font-medium text-sm" style={{ color: status.text }}>
                              {horaInicio} - {horaFim}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge 
                              className="text-xs"
                              style={{ backgroundColor: status.bg, color: status.text, border: `1px solid ${status.border}` }}
                            >
                              {ag.status?.replace('_', ' ')}
                            </Badge>
                            {canDelete && onDeleteClick && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-gray-400 hover:text-red-500 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteClick(ag);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="mt-1">
                          <p className="font-medium" style={{ color: '#2C3632' }}>
                            {ag.pacientes?.nome_completo || 'Paciente'}
                          </p>
                          <p className="text-xs" style={{ color: '#5C6B64' }}>
                            {ag.profissionais?.usuarios?.nome || 'Profissional'}
                            {ag.salas?.nome && ` • ${ag.salas.nome}`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AgendaCalendarView;

